/**
 * Foldable left navigation for dashboard primary actions (same targets as QuickActionsCard).
 *
 * @module DashboardSidebar
 */

import {
  BarChart3,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  Home,
  LogOut,
  Menu,
  MessageSquare,
  PanelLeft,
  PanelLeftClose,
  Settings,
  Shield,
  User,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { DashboardModule } from '@/data/dashboardData';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/routeConstants';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { logger } from '@/lib/logger';
import { APP_VERSION } from '@/lib/version';
import { navigateToSignedOutSignIn } from '@/lib/authNavigation';

const iconMap = {
  GraduationCap,
  ClipboardList,
  Users,
  BarChart3,
  MessageSquare,
  UserCheck,
} as const;

const EXPANDED_SIDEBAR_WIDTH = 'w-[17rem]';
const COLLAPSED_SIDEBAR_WIDTH = 'w-[4.5rem]';

export interface DashboardSidebarProps {
  readonly modules: readonly DashboardModule[];
  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;
  readonly className?: string;
}

function getUserInitials(username?: string | null, email?: string | null): string {
  const source = username?.trim() || email?.trim() || 'U';
  const parts = source
    .split(/[\s._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
}

interface SidebarNavRowProps {
  readonly collapsed: boolean;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly description?: string;
  readonly badgeLabel?: string | null;
  readonly disabled?: boolean;
  readonly showChevron?: boolean;
  readonly muted?: boolean;
  readonly onClick: () => void;
}

function SidebarNavRow({
  collapsed,
  icon: Icon,
  label,
  description,
  badgeLabel,
  disabled = false,
  showChevron = true,
  muted = false,
  onClick,
}: SidebarNavRowProps) {
  const buttonEl = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full rounded-lg border border-transparent text-left text-sm font-medium transition-colors',
        collapsed ? 'items-center justify-center p-1.5' : 'items-center gap-2.5 px-2 py-2',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'hover:border-border/60 hover:bg-accent/60',
        muted && !disabled && 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-lg border border-border/60 bg-background shadow-sm',
          collapsed ? 'h-11 w-11' : 'h-9 w-9',
          disabled && 'text-muted-foreground',
        )}
      >
        <Icon className={cn(collapsed ? 'h-5 w-5' : 'h-4 w-4')} aria-hidden />
      </span>
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-ui text-sm font-medium leading-tight">{label}</span>
            {badgeLabel ? (
              <span className="mt-0.5 block truncate font-ui text-xs font-normal text-primary">
                {badgeLabel}
              </span>
            ) : null}
          </span>
          {showChevron && !disabled ? (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          ) : (
            <span className="w-4 shrink-0" aria-hidden />
          )}
        </>
      )}
    </button>
  );

  if (!collapsed) {
    return buttonEl;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs font-ui">
        <p className="font-medium">{label}</p>
        {badgeLabel ? <p className="text-xs text-muted-foreground">{badgeLabel}</p> : null}
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}

function DashboardAccountSection({
  collapsed = false,
  onNavigate,
}: {
  readonly collapsed?: boolean;
  readonly onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const username = user?.username || t('nav.profile', { defaultValue: 'Profile' });
  const email = user?.email || t('dashboard.account.noEmail', { defaultValue: 'No email available' });
  const initials = getUserInitials(user?.username, user?.email);

  const handleProfileClick = () => {
    logger.event(
      'dashboard.account.profile_selected',
      { targetRoute: ROUTES.SETTINGS, settingsSection: 'general' },
      'info',
      'Dashboard profile action selected',
    );
    navigate({ pathname: ROUTES.SETTINGS, hash: 'general' });
    onNavigate?.();
  };

  const handleSettingsClick = () => {
    logger.event(
      'dashboard.account.settings_selected',
      { targetRoute: ROUTES.SETTINGS, settingsSection: 'security' },
      'info',
      'Dashboard settings action selected',
    );
    navigate({ pathname: ROUTES.SETTINGS, hash: 'security' });
    onNavigate?.();
  };

  const handleAdminClick = () => {
    logger.event(
      'dashboard.account.admin_selected',
      { targetRoute: ROUTES.ADMIN },
      'info',
      'Dashboard admin action selected',
    );
    navigate(ROUTES.ADMIN);
    onNavigate?.();
  };

  const handleLogoutClick = async () => {
    logger.event('dashboard.account.logout.started', {}, 'info', 'Dashboard logout action selected');
    try {
      await logout();
    } catch (error) {
      logger.error('Logout error', error, {
        event: 'dashboard.account.logout.failed',
      });
    } finally {
      navigateToSignedOutSignIn(navigate);
      onNavigate?.();
    }
  };

  const actions = [
    {
      id: 'profile',
      icon: User,
      label: t('nav.profile', { defaultValue: 'Profile' }),
      description: t('dashboard.account.profileDescription', {
        defaultValue: 'View your account details',
      }),
      onClick: handleProfileClick,
      showChevron: true,
    },
    {
      id: 'settings',
      icon: Settings,
      label: t('nav.settings', { defaultValue: 'Settings' }),
      description: t('dashboard.account.settingsDescription', {
        defaultValue: 'Manage security and preferences',
      }),
      onClick: handleSettingsClick,
      showChevron: true,
    },
    ...(user?.userType === 'admin'
      ? [
          {
            id: 'admin',
            icon: Shield,
            label: t('nav.admin', { defaultValue: 'Admin' }),
            description: t('dashboard.account.adminDescription', {
              defaultValue: 'Open administration tools',
            }),
            onClick: handleAdminClick,
            showChevron: true,
          },
        ]
      : []),
    {
      id: 'logout',
      icon: LogOut,
      label: t('nav.logout', { defaultValue: 'Logout' }),
      description: t('dashboard.account.logoutDescription', {
        defaultValue: 'End your current session',
      }),
      onClick: handleLogoutClick,
      showChevron: false,
      muted: true,
    },
  ];

  if (collapsed) {
    return (
      <div className="shrink-0 border-t border-border p-2">
        <div className="flex flex-col items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleProfileClick}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/70 bg-background shadow-sm transition-colors hover:bg-accent/60"
                aria-label={t('nav.profile', { defaultValue: 'Profile' })}
              >
                <Avatar className="h-9 w-9 border border-border/60">
                  <AvatarImage src={user?.profileImageUrl} alt={username} />
                  <AvatarFallback className="bg-primary/10 font-ui text-xs font-semibold text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs font-ui">
              <p className="font-medium">{username}</p>
              <p className="text-xs text-muted-foreground">{email}</p>
            </TooltipContent>
          </Tooltip>

          {actions.slice(1).map((action) => (
            <SidebarNavRow
              key={action.id}
              collapsed
              icon={action.icon}
              label={action.label}
              description={action.description}
              showChevron={false}
              muted={'muted' in action && action.muted}
              onClick={action.onClick}
            />
          ))}

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mt-1 rounded-full border border-border/60 bg-background px-2 py-0.5 font-ui text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                v{APP_VERSION}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-ui">
              <p className="font-medium">Version {APP_VERSION}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-t border-border">
      <div className="flex items-center gap-2.5 px-3 py-3">
        <Avatar className="h-9 w-9 shrink-0 border border-border/60">
          <AvatarImage src={user?.profileImageUrl} alt={username} />
          <AvatarFallback className="bg-primary/10 font-ui text-xs font-semibold text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate font-ui text-sm font-semibold text-foreground">{username}</p>
          <p className="truncate font-ui text-xs text-muted-foreground">{email}</p>
        </div>
      </div>

      <div className="space-y-0.5 px-2 pb-2">
        {actions.map((action) => (
          <div key={action.id}>
            {action.id === 'logout' ? <Separator className="my-1.5" /> : null}
            <SidebarNavRow
              collapsed={false}
              icon={action.icon}
              label={action.label}
              description={action.description}
              showChevron={action.showChevron}
              muted={'muted' in action && action.muted}
              onClick={action.onClick}
            />
          </div>
        ))}
      </div>

      <p className="border-t border-border/60 py-2 text-center font-ui text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        v{APP_VERSION}
      </p>
    </div>
  );
}

interface DashboardNavItemsProps {
  readonly modules: readonly DashboardModule[];
  readonly collapsed: boolean;
  readonly onDashboardClick: () => void;
  readonly onModuleClick: (module: DashboardModule) => void;
}

function DashboardNavItems({
  modules,
  collapsed,
  onDashboardClick,
  onModuleClick,
}: DashboardNavItemsProps) {
  const { t } = useTranslation();

  return (
    <>
      <SidebarNavRow
        collapsed={collapsed}
        icon={Home}
        label={t('nav.dashboard', { defaultValue: 'Dashboard' })}
        onClick={onDashboardClick}
      />

      {modules.map((module) => {
        const Icon = iconMap[module.icon as keyof typeof iconMap] || GraduationCap;
        const label = t(`dashboard.quickAccess.${module.id}`);
        const badgeLabel = module.badge ? t(`dashboard.quickAccess.${module.badge}`) : null;
        const description =
          module.disabled && module.disabledHint ? module.disabledHint : undefined;

        return (
          <SidebarNavRow
            key={module.id}
            collapsed={collapsed}
            icon={Icon}
            label={label}
            description={description}
            badgeLabel={badgeLabel}
            disabled={!!module.disabled}
            onClick={() => onModuleClick(module)}
          />
        );
      })}
    </>
  );
}

interface DashboardExpandedNavBodyProps {
  readonly modules: readonly DashboardModule[];
  readonly onDashboardClick: () => void;
  readonly onModuleClick: (module: DashboardModule) => void;
  readonly onNavigate?: () => void;
  readonly className?: string;
}

function DashboardExpandedNavBody({
  modules,
  onDashboardClick,
  onModuleClick,
  onNavigate,
  className,
}: DashboardExpandedNavBodyProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      <nav
        className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain p-2"
        aria-label={t('nav.dashboard')}
      >
        <DashboardNavItems
          modules={modules}
          collapsed={false}
          onDashboardClick={onDashboardClick}
          onModuleClick={onModuleClick}
        />
      </nav>

      <DashboardAccountSection onNavigate={onNavigate} />
    </div>
  );
}

export function DashboardSidebar({
  modules,
  collapsed,
  onToggleCollapsed,
  className,
}: DashboardSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSidebarToggle = () => {
    logger.event(
      'dashboard.sidebar.toggle',
      { collapsedNext: !collapsed },
      'info',
      'Dashboard sidebar toggle selected',
    );
    onToggleCollapsed();
  };

  const handleModuleClick = (module: DashboardModule) => {
    if (module.disabled || !module.route) {
      logger.event(
        'dashboard.navigation.blocked',
        {
          moduleId: module.id,
          targetRoute: module.route,
          disabled: module.disabled,
        },
        'warn',
        'Blocked dashboard navigation selection',
      );
      return;
    }
    logger.event(
      'dashboard.navigation.selected',
      {
        moduleId: module.id,
        targetRoute: module.route,
      },
      'info',
      'Dashboard navigation item selected',
    );
    navigate(module.route);
  };

  const handleDashboardHome = () => {
    logger.event(
      'dashboard.navigation.selected',
      {
        moduleId: 'dashboard',
        targetRoute: ROUTES.DASHBOARD,
      },
      'info',
      'Dashboard home navigation selected',
    );
    navigate(ROUTES.DASHBOARD);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex h-full min-h-0 flex-col border-r border-border bg-card/50',
          collapsed ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH,
          className,
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center border-b border-border px-2 py-2',
            collapsed ? 'justify-center' : 'justify-between gap-2 px-3',
          )}
        >
          {!collapsed ? (
            <p className="min-w-0 truncate font-ui text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dashboard.quickAccess.title')}
            </p>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={handleSidebarToggle}
            aria-expanded={!collapsed}
            aria-label={
              collapsed
                ? t('dashboard.sidebar.expand', { defaultValue: 'Expand navigation' })
                : t('dashboard.sidebar.collapse', { defaultValue: 'Collapse navigation' })
            }
          >
            {collapsed ? (
              <PanelLeft className="h-5 w-5" aria-hidden />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden />
            )}
          </Button>
        </div>

        {collapsed ? (
          <>
            <nav
              className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain p-2"
              aria-label={t('nav.dashboard')}
            >
              <DashboardNavItems
                modules={modules}
                collapsed
                onDashboardClick={handleDashboardHome}
                onModuleClick={handleModuleClick}
              />
            </nav>

            <DashboardAccountSection collapsed />
          </>
        ) : (
          <DashboardExpandedNavBody
            modules={modules}
            onDashboardClick={handleDashboardHome}
            onModuleClick={handleModuleClick}
          />
        )}
      </aside>
    </TooltipProvider>
  );
}

export function DashboardNavMenuButton({
  onOpen,
  className,
}: {
  readonly onOpen: () => void;
  readonly className?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={cn('lg:hidden', className)}
      onClick={onOpen}
      aria-label={t('dashboard.sidebar.openMenu', { defaultValue: 'Open menu' })}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}

export function DashboardNavList({
  modules,
  onNavigate,
}: {
  readonly modules: readonly DashboardModule[];
  readonly onNavigate: () => void;
}) {
  const navigate = useNavigate();

  const handleModuleClick = (module: DashboardModule) => {
    if (module.disabled || !module.route) {
      return;
    }
    navigate(module.route);
    onNavigate();
  };

  return (
    <DashboardExpandedNavBody
      modules={modules}
      onDashboardClick={() => {
        navigate(ROUTES.DASHBOARD);
        onNavigate();
      }}
      onModuleClick={handleModuleClick}
      onNavigate={onNavigate}
    />
  );
}
